/**
 * AdminCertificates — Admin dashboard for the certificate registry.
 *
 * Shows:
 *   - Top counters (total issued / active / revoked / distinct courses)
 *   - Per-course completion breakdown (unique-user counts + total certs)
 *   - Full searchable/filterable list of certificates with signature-check
 *     + revoke action per row
 *
 * Anti-forge angle: forged (photoshopped) certificate numbers cannot exist
 * in this table. If a customer support ticket cites a cert number, the
 * admin looks it up here. Missing → forgery. Present + revoked → forgery.
 * Present + active + signature_valid → genuine.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Award, ShieldCheck, ShieldAlert, Search, RotateCcw, GraduationCap,
  Users, ArrowLeft, ArrowUpRight, XCircle, Loader2, TrendingUp, Settings, Save,
  Sparkles,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch (_) { return iso; }
};

const StatCard = ({ label, value, icon: Icon, tone = 'primary', testId }) => (
  <div
    className={`pro-card p-5 flex items-center gap-4 ${
      tone === 'destructive' ? 'border-destructive/30 bg-destructive/5'
      : tone === 'emerald' ? 'border-emerald-500/30 bg-emerald-500/5'
      : 'border-primary/30 bg-primary/5'
    }`}
    data-testid={testId}
  >
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
      tone === 'destructive' ? 'bg-destructive/10 text-destructive'
      : tone === 'emerald' ? 'bg-emerald-500/10 text-emerald-700'
      : 'bg-primary/10 text-primary'
    }`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="font-heading text-3xl font-extrabold text-foreground tracking-tight leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-foreground/60 mt-1">{label}</div>
    </div>
  </div>
);

const AdminCertificates = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [auditTarget, setAuditTarget] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  // Platform-level signature defaults
  const [settings, setSettings] = useState({ academy_director: '', default_instructor: '' });
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Specialty mint dialog
  const [specialtyOpen, setSpecialtyOpen] = useState(false);
  const [specialty, setSpecialty] = useState({ user_email: '', specialty_title: '', event_program: '', description: '' });
  const [specialtySaving, setSpecialtySaving] = useState(false);

  const authHeaders = useMemo(() => {
    const t = window.localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status_filter = statusFilter;
      const res = await axios.get(`${API}/admin/certificates`, { headers: authHeaders, params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load certificates');
    } finally { setLoading(false); }
  }, [API, authHeaders, q, statusFilter]);

  const loadRef = useMemo(() => load, [load]); // stable ref for effect dependency
  useEffect(() => { load(); }, [statusFilter, loadRef]);

  // Load platform-level cert defaults once on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/certificate-settings`, { headers: authHeaders });
        setSettings({
          academy_director: res.data?.academy_director || '',
          default_instructor: res.data?.default_instructor || '',
        });
      } catch (_) { /* silent — non-fatal */ }
    })();
  }, [API, authHeaders]);

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await axios.put(`${API}/admin/certificate-settings`, settings, { headers: authHeaders });
      toast.success('Default signatures updated. Applied to all future certificates.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSettingsSaving(false); }
  };

  const mintSpecialty = async () => {
    if (!specialty.user_email || !specialty.specialty_title || !specialty.event_program) {
      toast.error('Fill in recipient email, specialty title, and event/program');
      return;
    }
    setSpecialtySaving(true);
    try {
      const res = await axios.post(`${API}/admin/specialty-certificate`, specialty, { headers: authHeaders });
      toast.success(`Specialty certificate ${res.data.cert_number} issued to ${specialty.user_email}`);
      setSpecialtyOpen(false);
      setSpecialty({ user_email: '', specialty_title: '', event_program: '', description: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mint failed');
    } finally { setSpecialtySaving(false); }
  };

  const revoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await axios.post(
        `${API}/admin/certificates/${revokeTarget.cert_number}/revoke`,
        { reason: revokeReason || 'Revoked by administrator' },
        { headers: authHeaders },
      );
      toast.success(`Revoked ${revokeTarget.cert_number}`);
      setRevokeTarget(null); setRevokeReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    } finally { setRevoking(false); }
  };

  const runAudit = async (cert) => {
    setAuditTarget(cert); setAudit(null); setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/admin/certificates/${cert.cert_number}/audit`, { headers: authHeaders });
      setAudit(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audit failed');
      setAuditTarget(null);
    } finally { setAuditLoading(false); }
  };

  return (
    <div className="min-h-screen bg-muted" data-testid="admin-certificates-page">
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/admin')} data-testid="admin-certs-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Admin
          </Button>
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            <h1 className="font-heading text-lg font-extrabold text-foreground tracking-tight">Certificate registry</h1>
          </div>
          <div className="w-24" />
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Platform-level signature defaults */}
        <div className="pro-card p-6 mb-8" data-testid="cert-settings-panel">
          <h2 className="font-heading text-lg font-extrabold text-foreground tracking-tight mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" /> Certificate signatures
          </h2>
          <p className="text-sm text-foreground/70 mb-5 max-w-2xl">
            These names appear on every new certificate. Individual courses can override the instructor name in the LMS course editor
            (falls back to <span className="font-mono font-bold">FDAcademy Faculty</span> if unset).
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-[0.14em] text-foreground/60">Academy Director</label>
              <Input
                value={settings.academy_director}
                onChange={(e) => setSettings((s) => ({ ...s, academy_director: e.target.value }))}
                placeholder="e.g. Dr. Rachel Nguyen"
                data-testid="cert-setting-director-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-[0.14em] text-foreground/60">Default Instructor</label>
              <Input
                value={settings.default_instructor}
                onChange={(e) => setSettings((s) => ({ ...s, default_instructor: e.target.value }))}
                placeholder="e.g. FDAcademy Faculty"
                data-testid="cert-setting-instructor-input"
              />
            </div>
          </div>
          <div className="mt-5 flex justify-between items-center flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => setSpecialtyOpen(true)}
              className="rounded-full font-bold"
              data-testid="mint-specialty-btn"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Mint specialty certificate
            </Button>
            <Button
              onClick={saveSettings}
              disabled={settingsSaving}
              className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="cert-setting-save-btn"
            >
              <Save className="w-4 h-4 mr-1.5" /> {settingsSaving ? 'Saving…' : 'Save signatures'}
            </Button>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total minted" value={data?.totals?.all ?? '—'} icon={GraduationCap} tone="primary" testId="stat-total" />
          <StatCard label="Active" value={data?.totals?.active ?? '—'} icon={ShieldCheck} tone="emerald" testId="stat-active" />
          <StatCard label="Revoked" value={data?.totals?.revoked ?? '—'} icon={ShieldAlert} tone="destructive" testId="stat-revoked" />
          <StatCard label="Courses with completions" value={data?.totals?.courses_with_completions ?? '—'} icon={TrendingUp} tone="primary" testId="stat-courses" />
        </div>

        {/* Per-course table */}
        <div className="pro-card p-6 mb-8" data-testid="per-course-table">
          <h2 className="font-heading text-lg font-extrabold text-foreground tracking-tight mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Completions per course
          </h2>
          {loading && <div className="text-sm text-foreground/60">Loading…</div>}
          {!loading && (data?.per_course || []).length === 0 && (
            <div className="text-sm text-foreground/60">No certificates issued yet.</div>
          )}
          {!loading && (data?.per_course || []).length > 0 && (
            <div className="space-y-2">
              {data.per_course.map((row) => (
                <div
                  key={row.course_id}
                  className="flex items-center gap-4 py-2 border-b border-border last:border-b-0"
                  data-testid={`per-course-row-${row.course_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-extrabold text-foreground tracking-tight truncate">{row.course_title}</div>
                    <div className="text-xs text-foreground/60 font-mono truncate">{row.course_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-2xl font-extrabold text-primary">{row.count}</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-foreground/60">
                      {row.unique_users} unique
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search + filter */}
        <div className="pro-card p-5 mb-4 flex flex-wrap items-center gap-3" data-testid="cert-filter-bar">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
            <Input
              placeholder="Search cert number, holder, email, course…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              className="pl-10"
              data-testid="cert-search-input"
            />
          </div>
          <div className="flex items-center gap-1 rounded-full border border-border p-1 bg-background">
            {[['', 'All'], ['active', 'Active'], ['revoked', 'Revoked']].map(([val, label]) => (
              <button
                key={val || 'all'}
                onClick={() => setStatusFilter(val)}
                className={`px-3 py-1 text-xs font-extrabold rounded-full transition-colors ${statusFilter === val ? 'bg-primary text-primary-foreground' : 'text-foreground/60 hover:text-foreground'}`}
                data-testid={`cert-filter-${val || 'all'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} className="rounded-full font-bold" data-testid="cert-search-btn">
            <Search className="w-3.5 h-3.5 mr-1.5" /> Search
          </Button>
        </div>

        {/* List */}
        <div className="pro-card overflow-hidden" data-testid="cert-list">
          <div className="grid grid-cols-[1fr_1fr_120px_140px_180px] gap-3 px-5 py-3 border-b border-border text-[10px] uppercase tracking-[0.14em] font-extrabold text-foreground/60 bg-muted/50">
            <div>Certificate #</div>
            <div>Holder / Course</div>
            <div>Issued</div>
            <div>Status</div>
            <div className="text-right">Actions</div>
          </div>
          {loading && (
            <div className="p-10 text-center text-foreground/60" data-testid="cert-list-loading">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
            </div>
          )}
          {!loading && (data?.certificates || []).length === 0 && (
            <div className="p-10 text-center text-foreground/60" data-testid="cert-list-empty">No certificates found for these filters.</div>
          )}
          {!loading && (data?.certificates || []).map((c) => (
            <div
              key={c.cert_number}
              className="grid grid-cols-[1fr_1fr_120px_140px_180px] gap-3 px-5 py-4 border-b border-border last:border-b-0 items-center hover:bg-muted/40 transition-colors"
              data-testid={`cert-row-${c.cert_number}`}
            >
              <div className="min-w-0">
                <div className="font-mono text-xs font-extrabold text-foreground truncate">{c.cert_number}</div>
                <div className="text-[10px] text-foreground/50 mt-0.5">Course: {c.course_id.slice(0, 8)}…</div>
              </div>
              <div className="min-w-0">
                <div className="font-bold text-sm truncate">{c.holder_name}</div>
                <div className="text-xs text-foreground/60 truncate">{c.holder_email} · {c.course_title}</div>
              </div>
              <div className="text-xs text-foreground/70">{fmt(c.issued_at)}</div>
              <div>
                {c.revoked ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 text-destructive text-[10px] font-extrabold px-2.5 py-1">
                    <ShieldAlert className="w-3 h-3" /> Revoked
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 text-[10px] font-extrabold px-2.5 py-1">
                    <ShieldCheck className="w-3 h-3" /> Active
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runAudit(c)}
                  className="rounded-full font-bold text-xs h-8 px-3"
                  data-testid={`cert-audit-${c.cert_number}`}
                >
                  Audit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(`/certificate/${c.cert_number}`, '_blank', 'noopener')}
                  className="rounded-full font-bold text-xs h-8 px-3"
                  data-testid={`cert-view-${c.cert_number}`}
                >
                  <ArrowUpRight className="w-3 h-3" />
                </Button>
                {!c.revoked && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRevokeTarget(c)}
                    className="rounded-full font-bold text-xs h-8 px-3 text-destructive hover:bg-destructive/10"
                    data-testid={`cert-revoke-${c.cert_number}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Specialty mint dialog */}
      <Dialog open={specialtyOpen} onOpenChange={setSpecialtyOpen}>
        <DialogContent data-testid="specialty-dialog">
          <DialogHeader>
            <DialogTitle>Mint Specialty Certificate</DialogTitle>
            <DialogDescription>
              Awards a Specialty Certificate to any user for a special event, workshop, or focused learning session.
              Bypasses the anti-cheat gate — admin discretion.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-[0.14em] text-foreground/60">Recipient Email</label>
              <Input
                type="email"
                value={specialty.user_email}
                onChange={(e) => setSpecialty((s) => ({ ...s, user_email: e.target.value }))}
                placeholder="learner@example.com"
                data-testid="specialty-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-[0.14em] text-foreground/60">Specialty Title</label>
              <Input
                value={specialty.specialty_title}
                onChange={(e) => setSpecialty((s) => ({ ...s, specialty_title: e.target.value }))}
                placeholder="e.g. Advanced Vacuum Optimization"
                data-testid="specialty-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-[0.14em] text-foreground/60">Event / Program</label>
              <Input
                value={specialty.event_program}
                onChange={(e) => setSpecialty((s) => ({ ...s, event_program: e.target.value }))}
                placeholder="e.g. Freeze-dry masterclass — Denver, Apr 12"
                data-testid="specialty-event-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-[0.14em] text-foreground/60">Description (optional)</label>
              <Textarea
                value={specialty.description}
                onChange={(e) => setSpecialty((s) => ({ ...s, description: e.target.value }))}
                placeholder="Optional context that appears on the printable cert"
                rows={2}
                data-testid="specialty-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSpecialtyOpen(false)} data-testid="specialty-cancel-btn">Cancel</Button>
            <Button
              onClick={mintSpecialty}
              disabled={specialtySaving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="specialty-mint-btn"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> {specialtySaving ? 'Minting…' : 'Mint specialty certificate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <DialogContent data-testid="revoke-dialog">
          <DialogHeader>
            <DialogTitle>Revoke certificate</DialogTitle>
            <DialogDescription>
              This will immediately mark the certificate as revoked. Future public verifications will return
              <span className="font-bold text-destructive"> valid: false</span> with the reason you enter below. This action is reversible only by editing the DB.
            </DialogDescription>
          </DialogHeader>
          {revokeTarget && (
            <div className="text-sm space-y-3 py-2">
              <div><span className="text-foreground/60">Cert #:</span> <span className="font-mono font-extrabold">{revokeTarget.cert_number}</span></div>
              <div><span className="text-foreground/60">Holder:</span> <span className="font-bold">{revokeTarget.holder_name}</span> ({revokeTarget.holder_email})</div>
              <div><span className="text-foreground/60">Course:</span> {revokeTarget.course_title}</div>
              <div className="space-y-1.5">
                <label className="text-xs font-extrabold uppercase tracking-[0.14em] text-foreground/60">Reason</label>
                <Textarea
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="e.g. Forged submission, cheating detected on quiz retakes…"
                  rows={3}
                  data-testid="revoke-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)} data-testid="revoke-cancel-btn">Cancel</Button>
            <Button onClick={revoke} disabled={revoking} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="revoke-confirm-btn">
              {revoking ? 'Revoking…' : 'Revoke certificate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit dialog */}
      <Dialog open={!!auditTarget} onOpenChange={(o) => !o && (setAuditTarget(null), setAudit(null))}>
        <DialogContent data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle>Certificate audit</DialogTitle>
            <DialogDescription>Signature check + evidence pulled from the learner&apos;s progress record.</DialogDescription>
          </DialogHeader>
          {auditLoading && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
          {audit && (
            <div className="text-sm space-y-3 py-2">
              <div className="flex items-center gap-2">
                {audit.signature_valid ? (
                  <><ShieldCheck className="w-5 h-5 text-emerald-600" /> <span className="font-extrabold text-emerald-700">Signature valid</span></>
                ) : (
                  <><XCircle className="w-5 h-5 text-destructive" /> <span className="font-extrabold text-destructive">Signature INVALID — likely forgery</span></>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-foreground/60">Expected:</span><br/><span className="font-mono">{audit.signature_expected_prefix}…</span></div>
                <div><span className="text-foreground/60">Stored:</span><br/><span className="font-mono">{audit.signature_stored_prefix}…</span></div>
              </div>
              <div className="border-t border-border pt-3 space-y-1">
                <div><span className="text-foreground/60">Lessons:</span> {audit.evidence.lessons_completed}/{audit.evidence.lessons_total}</div>
                <div><span className="text-foreground/60">Verified time:</span> {Math.round(audit.evidence.verified_time_seconds_total / 60)} minutes</div>
                <div><span className="text-foreground/60">Quiz attempts passed:</span> {audit.evidence.quiz_attempts_passed}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => (setAuditTarget(null), setAudit(null))} data-testid="audit-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCertificates;
